import { startTransition, useCallback, useMemo } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@reflex/ui/components/ui/tooltip';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { AgentSidebar } from '@/components/agent/AgentSidebar';
import { AgentListFilterContext } from '@/components/agent/agent-list-filter-context';
import { useAgentFilters } from '@/hooks/agents/useAgentFilters';
import { useAuth } from '@/hooks/auth/useAuth';
import { useActiveOrgSlug, useOrgRoutes } from '@/lib/useOrgRoutes';
import { ORG_PATH_PREFIX } from '@/lib/routes';
import { launchDialogDraftId, withLaunchDialogQuery } from '@/pages/agents/launch-dialog-url';
import type { SidebarSectionContext } from '@/lib/agent-utils';
import type { Agent, AgentDraft } from '@/lib/types';
import type { WsConnectionState } from '@/lib/socketManager';
import { cn } from '@reflex/ui/lib/utils';
import type { NavItem } from '../NavBar/nav-types';
import { getActiveDrillItemPath } from '../NavBar/nav-utils';
import { useNavSources } from '../NavBar/useNavSources';
import { OrgSwitcher } from '../NavBar/OrgSwitcher';
import { InboxLink, UserSection } from '../NavBar/UserSection';
import { NavUtilityElements } from '../NavBar/NavUtilityElements';
import { useConfigureEntry } from '../ConfigureShell';
import { isConfigurePath } from '../ConfigureShell/configure-paths';
import { isAdminPath } from '../TakeoverShell';

interface SessionDockProps {
  onToggle: () => void;
  /** Unused here — connection status surfaces on the collapsed rail. */
  wsState: WsConnectionState;
  onCommandOpen: () => void;
}

/**
 * The `sessions` nav style, and the app's default shell: the session list is
 * the sidebar. The org selector heads the bar with the launch button beside
 * it, the combined search + filter bar sits underneath (`AgentSidebar`'s
 * dock variant), and everything else lives in one bottom bar: the takeover
 * doors (Automations, Labs) as labeled entries, then the remaining
 * destinations, inbox, and the account menu as icons, with the Configure
 * gear pinned at the end of the utility row. Reuses
 * `AgentSidebar` wholesale with router-driven selection, so filters, pins,
 * drafts, and multi-select behave exactly as on the sessions page.
 *
 * Collapsing the panel falls back to the standard icon rail
 * (`NavBarCollapsed`), which acts as the focus mode.
 */
/** Icon-only strip link with a tooltip naming the destination. */
function IconStripLink({ item, active }: { item: NavItem; active: boolean }) {
  const { to, label, icon: Icon, tutorialId } = item;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={to}
          aria-label={label}
          aria-current={active ? 'page' : undefined}
          data-testid={`dock-strip-${to}`}
          data-tutorial-id={tutorialId}
          className={cn(
            'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
            active
              ? 'bg-selected text-selected-foreground'
              : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
          )}
        >
          <Icon className="h-4 w-4" />
        </Link>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

export function SessionDock({ onToggle, onCommandOpen }: SessionDockProps) {
  const { user } = useAuth();
  const agentFilters = useAgentFilters(user?.id ?? null);
  const routes = useOrgRoutes();
  const slug = useActiveOrgSlug();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    allMainNavItems,
    mainDrillGroups,
    bottomDrillGroups,
    pluginBottomItems,
    takeoverBottomItems,
    labsItem,
    adminItem,
  } = useNavSources();
  const configureEntry = useConfigureEntry();

  const agentMatch = useMatch(`/${ORG_PATH_PREFIX}/:orgSlug/agents/:agentId`);
  const runsMatch = useMatch(`/${ORG_PATH_PREFIX}/:orgSlug/runs`);
  const onAgentsSurface = agentMatch !== null || runsMatch !== null;
  const selectedId = agentMatch?.params.agentId ?? null;
  const selectedDraftId = onAgentsSurface ? launchDialogDraftId(location.search) : null;

  const handleSelect = useCallback(
    (agent: Agent) => {
      // Same contract as AgentsPage: drop inherited search params so the
      // agent opens on its default tab, and keep the heavy stream mount
      // interruptible.
      startTransition(() => {
        navigate({ pathname: routes.agent(agent.id), search: '' });
      });
    },
    [navigate, routes],
  );

  // Same contract as AgentsPage: land on the run the sidebar nominates rather
  // than the index, which would paint the dashboard for a frame before its
  // auto-select effect redirected into that very run.
  const handleDeselect = useCallback(
    (next?: Agent) =>
      navigate(next ? { pathname: routes.agent(next.id), search: '' } : routes.runs),
    [navigate, routes],
  );

  // The launch dialog is mounted by AgentsPage and driven by `?new=true`;
  // from any other surface, opening it means navigating there first.
  const handleLaunchAgent = useCallback(() => {
    // Replace when already on the agents surface so dialog state stays a
    // pure function of the URL without stacking history entries (the same
    // contract as AgentsPage's setLaunchDialogOpen); push only when
    // arriving from another surface so Back returns there.
    navigate(
      {
        pathname: onAgentsSurface ? location.pathname : routes.runs,
        search: withLaunchDialogQuery(onAgentsSurface ? location.search : ''),
      },
      { replace: onAgentsSurface },
    );
  }, [navigate, onAgentsSurface, location.pathname, location.search, routes]);

  // The per-group "+" in the session list. Same navigation contract as
  // `handleLaunchAgent`, plus the group the heading stands for; any `?draft`
  // is cleared, since a draft remembers a group of its own.
  const handleLaunchInGroup = useCallback(
    (group: SidebarSectionContext) => {
      navigate(
        {
          pathname: onAgentsSurface ? location.pathname : routes.runs,
          search: withLaunchDialogQuery(onAgentsSurface ? location.search : '', null, group.id),
        },
        { replace: onAgentsSurface },
      );
    },
    [navigate, onAgentsSurface, location.pathname, location.search, routes],
  );

  const handleSelectDraft = useCallback(
    (draft: AgentDraft) => {
      navigate(
        {
          pathname: onAgentsSurface ? location.pathname : routes.runs,
          search: withLaunchDialogQuery(onAgentsSurface ? location.search : '', draft.id),
        },
        { replace: onAgentsSurface },
      );
    },
    [navigate, onAgentsSurface, location.pathname, location.search, routes],
  );

  // Same provider AgentsPage builds: group/label chips inside rows filter the
  // list. The filters share their per-tab storage key, so both instances
  // stay in step.
  const listFilterActions = useMemo(
    () => ({
      activeGroupId: agentFilters.filters.groupId,
      setGroupFilter: agentFilters.setGroupId,
      activeLabelId: agentFilters.filters.labelId,
      setLabelFilter: agentFilters.setLabelId,
    }),
    [
      agentFilters.filters.groupId,
      agentFilters.setGroupId,
      agentFilters.filters.labelId,
      agentFilters.setLabelId,
    ],
  );

  // Everything that is not the session list lives in the bottom bar. The
  // takeover doors render as labeled entries; the rest — stray main work
  // surfaces (minus Sessions, which the dock itself embodies), remaining
  // drill overviews, and plugin bottom items — as icons. Configure is
  // pinned separately at the end of the utility row, not in this list.
  const iconItems = useMemo<NavItem[]>(
    () => [
      ...allMainNavItems.filter((item) => item.to !== routes.runs),
      ...[...mainDrillGroups, ...bottomDrillGroups].map((group) => ({
        to: group.basePath,
        label: group.label,
        icon: group.icon,
      })),
      ...pluginBottomItems,
    ],
    [allMainNavItems, routes.runs, mainDrillGroups, bottomDrillGroups, pluginBottomItems],
  );

  const activeStripPath = getActiveDrillItemPath(
    [...takeoverBottomItems, ...iconItems],
    location.pathname,
  );

  // Automations anchors the left of the doors row; Connect is pushed to
  // the right with a flex spacer between them.
  const connectItem = takeoverBottomItems.find((i) => i.label === 'Connect') ?? null;
  const otherDoorItems = takeoverBottomItems.filter((i) => i !== connectItem);

  return (
    <nav
      aria-label="Main navigation"
      className="flex h-full w-full min-w-0 flex-col bg-sidebar"
      data-testid="session-dock"
    >
      {/* `h-11` keeps a shared baseline with `PanelHeader`, but this row is
          app chrome — quieter type, hairline rule, brand launch as the only
          accent — so it does not twin the agent session title beside it. */}
      <div
        className="flex h-11 shrink-0 items-center gap-0.5 border-b border-sidebar-border pl-2.5 pr-1.5"
        data-testid="session-dock-header"
      >
        {/* The mark is the dock's "back to the session list" affordance: the
            sessions surface is this shell's home, so it points at runs rather
            than the org index (which redirects there anyway). */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={routes.runs}
              // Already on the sessions surface, the mark is a no-op
              // destination: replace so repeat clicks do not stack history,
              // the same contract as the launch handlers above.
              replace={runsMatch !== null}
              aria-label="Sessions"
              aria-current={runsMatch !== null ? 'page' : undefined}
              data-testid="dock-sessions"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-sidebar-accent"
            >
              <ReflexMark variant="tile" />
            </Link>
          </TooltipTrigger>
          <TooltipContent>Sessions</TooltipContent>
        </Tooltip>
        <div className="min-w-0 flex-1">
          <OrgSwitcher />
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="shrink-0 text-muted-foreground/70 hover:text-muted-foreground"
              onClick={onCommandOpen}
              aria-label="Search"
            >
              <Search className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Search ⌘K</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className="brand-hollow shrink-0 bg-brand-ink/10 text-brand-ink hover:bg-brand-ink/[0.16] hover:text-brand-ink dark:hover:bg-brand-ink/[0.16]"
              onClick={handleLaunchAgent}
              aria-label="New agent"
              data-testid="dock-launch"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>New agent</TooltipContent>
        </Tooltip>
      </div>

      {/* Carries the intro tutorial's Agents anchor: in this style the whole
          list is the "back to sessions" affordance the tour points at. */}
      <div className="min-h-0 flex-1" data-tutorial-id="nav-agents">
        <AgentListFilterContext.Provider value={listFilterActions}>
          <AgentSidebar
            selectedId={selectedId}
            selectedDraftId={selectedDraftId}
            onSelect={handleSelect}
            onSelectDraft={handleSelectDraft}
            onDeselect={handleDeselect}
            onToggle={onToggle}
            onLaunchAgent={handleLaunchAgent}
            onLaunchInGroup={handleLaunchInGroup}
            variant="dock"
          />
        </AgentListFilterContext.Provider>
      </div>

      <div
        className="shrink-0 border-t border-sidebar-border px-1.5 py-1.5"
        data-testid="session-dock-strip"
      >
        {/* Doors row: Automations on the left, Connect pushed to the right. */}
        {takeoverBottomItems.length > 0 && (
          <div className="flex items-center gap-0.5">
            {otherDoorItems.map(({ to, label, icon: Icon, tutorialId }) => {
              const active = to === activeStripPath;
              return (
                <Link
                  key={to}
                  to={to}
                  aria-current={active ? 'page' : undefined}
                  data-testid={`dock-strip-${to}`}
                  data-tutorial-id={tutorialId}
                  className={cn(
                    'inline-flex h-7 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-xs font-medium transition-colors',
                    active
                      ? 'bg-selected text-selected-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
                  )}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{label}</span>
                </Link>
              );
            })}
            {connectItem && (
              <>
                <div className="min-w-0 flex-1" />
                {(() => {
                  const active = connectItem.to === activeStripPath;
                  const ConnectIcon = connectItem.icon;
                  return (
                    <Link
                      to={connectItem.to}
                      aria-current={active ? 'page' : undefined}
                      data-testid={`dock-strip-${connectItem.to}`}
                      data-tutorial-id={connectItem.tutorialId}
                      className={cn(
                        'inline-flex h-7 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-xs font-medium transition-colors',
                        active
                          ? 'bg-selected text-selected-foreground'
                          : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground',
                      )}
                    >
                      <span className="truncate">{connectItem.label}</span>
                      <ConnectIcon className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  );
                })()}
              </>
            )}
          </div>
        )}
        {/* Utility row: the account menu, inbox, and plugin utility icons
            anchor the left; Labs/Admin/Configure sit on the right. Labs is
            directly left of the Admin shield. */}
        <div className={cn('flex items-center gap-0.5', takeoverBottomItems.length > 0 && 'mt-1')}>
          <UserSection bar />
          {slug && <InboxLink slug={slug} compact />}
          <NavUtilityElements compact />
          <div className="min-w-0 flex-1" />
          {iconItems.map((item) => (
            <IconStripLink key={item.to} item={item} active={item.to === activeStripPath} />
          ))}
          <IconStripLink item={labsItem} active={labsItem.to === activeStripPath} />
          {adminItem && <IconStripLink item={adminItem} active={isAdminPath(location.pathname)} />}
          <IconStripLink item={configureEntry} active={isConfigurePath(location.pathname)} />
        </div>
      </div>
    </nav>
  );
}
